import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ThreeSixty as ThreeSixtyIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  Water as WaterIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { cpFittingCategories } from "./cpFittingsData";
import { CPFittingCategory } from "./cpFittingsTypes";
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from "../utils";
import TileViewer360 from "../Tiles/TileViewer360";

const CPFittingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFitting, setSelectedFitting] = useState<CPFittingCategory | null>(null);
  const [view360, setView360] = useState<CPFittingCategory | null>(null);

  const handle360View = (fitting: CPFittingCategory) => {
    if (fitting.images360) {
      setView360(fitting);
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/gallery')}
            sx={{
              color: "#1a237e",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
          >
            Back to Gallery
          </Button>
        </Box>

        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#1a237e",
              mb: 2,
              background: "linear-gradient(45deg, #1a237e 30%, #3f51b5 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            CP Fittings Collection
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "#666",
              mb: 2,
              fontWeight: "300",
            }}
          >
            Premium Chrome Plated Fittings for Modern Spaces
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#777",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Explore our comprehensive range of CP fittings including kitchen taps, bathroom mixers, shower systems, and specialty fittings. 
            Each category features premium materials, advanced functionality, and elegant designs to enhance your living spaces.
          </Typography>

          {/* Feature Highlights */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 4, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BuildIcon sx={{ color: "#1a237e" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                Premium Brass Construction
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WaterIcon sx={{ color: "#1a237e" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                Water Saving Technology
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SecurityIcon sx={{ color: "#1a237e" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                5-10 Year Warranty
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Categories Grid */}
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: "bold",
            color: "#1a237e",
            mb: 4,
            textAlign: "center",
          }}
        >
          CP Fitting Categories by Application
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 4,
          }}
        >
          {cpFittingCategories.map((fitting) => (
            <Box key={fitting.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 24px rgba(26, 35, 126, 0.15)",
                  },
                  borderRadius: "16px",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={() => setSelectedFitting(fitting)}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 200,
                    background: getCategoryGradient("CP Fittings"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" mb={1}>
                      {fitting.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {fitting.primaryUse[0]}
                    </Typography>
                  </Box>
                </CardMedia>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    {fitting.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    sx={{ lineHeight: 1.5 }}
                  >
                    {fitting.description}
                  </Typography>

                  {/* Key Characteristics */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                    {fitting.keyCharacteristics.slice(0, 3).map((char, charIndex) => (
                      <Typography
                        key={charIndex}
                        variant="caption"
                        sx={{
                          backgroundColor: "#f0f2ff",
                          color: "#1a237e",
                          px: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFitting(fitting);
                      }}
                      sx={{
                        borderColor: "#1a237e",
                        color: "#1a237e",
                        "&:hover": {
                          backgroundColor: "#f0f2ff",
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<WhatsAppIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(`${fitting.name} CP Fitting inquiry`);
                      }}
                      sx={{
                        backgroundColor: "#25d366",
                        "&:hover": {
                          backgroundColor: "#1eb855",
                        },
                      }}
                    >
                      Inquiry
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Detailed Fitting Modal */}
        <Dialog
          open={!!selectedFitting}
          onClose={() => setSelectedFitting(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px" },
          }}
        >
          {selectedFitting && (
            <>
              <DialogTitle
                sx={{
                  background: getCategoryGradient("CP Fittings"),
                  color: "white",
                  position: "relative",
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  {selectedFitting.name}
                </Typography>
                <IconButton
                  onClick={() => setSelectedFitting(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, p: 3 }}>
                  {/* Left Column */}
                  <Box>
                    <Box
                      sx={{
                        height: 300,
                        background: getCategoryGradient("CP Fittings"),
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <BuildIcon sx={{ fontSize: "4rem", mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {selectedFitting.name}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Key Characteristics
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                      {selectedFitting.keyCharacteristics.map((char, index) => (
                        <Chip
                          key={index}
                          label={char}
                          size="small"
                          sx={{
                            backgroundColor: "#f0f2ff",
                            color: "#1a237e",
                          }}
                        />
                      ))}
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Materials & Construction
                    </Typography>
                    <List dense>
                      {selectedFitting.commonMaterials.map((material, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={material}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Right Column */}
                  <Box>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Description
                    </Typography>
                    <Typography variant="body1" mb={3} sx={{ lineHeight: 1.6 }}>
                      {selectedFitting.description}
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Primary Applications
                    </Typography>
                    <List dense>
                      {selectedFitting.primaryUse.map((use, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={use}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Specifications
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" mb={1}>
                        <strong>Material:</strong> {selectedFitting.specifications.material}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Flow Rate:</strong> {selectedFitting.specifications.flowRate}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Pressure:</strong> {selectedFitting.specifications.pressure}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Warranty:</strong> {selectedFitting.warranty}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Use Cases Section */}
                <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                  <Typography variant="h6" fontWeight="bold" mb={3}>
                    Recommended Use Cases
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {selectedFitting.useCases.map((useCase, index) => (
                      <Paper key={index} sx={{ p: 2, borderRadius: "8px" }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                          {useCase.location}
                        </Typography>
                        <Typography variant="body2" mb={2} sx={{ lineHeight: 1.5 }}>
                          {useCase.recommendation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Key Checks:</strong> {useCase.keyChecks.join(", ")}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                  onClick={() =>
                    handleShare({
                      title: selectedFitting.name,
                      description: selectedFitting.description,
                    })
                  }
                  startIcon={<ShareIcon />}
                  sx={{ color: "#666" }}
                >
                  Share
                </Button>
                {selectedFitting.images360 && (
                  <Button
                    onClick={() => handle360View(selectedFitting)}
                    startIcon={<ThreeSixtyIcon />}
                    variant="outlined"
                    sx={{
                      borderColor: "#1a237e",
                      color: "#1a237e",
                    }}
                  >
                    360° View
                  </Button>
                )}
                <Button
                  onClick={() => handleWhatsApp(`${selectedFitting.name} CP Fitting inquiry`)}
                  startIcon={<WhatsAppIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "#25d366",
                    "&:hover": {
                      backgroundColor: "#1eb855",
                    },
                  }}
                >
                  WhatsApp Inquiry
                </Button>
                <Button
                  onClick={handleCall}
                  startIcon={<PhoneIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "#1a237e",
                    "&:hover": {
                      backgroundColor: "#0d47a1",
                    },
                  }}
                >
                  Call Now
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* 360 View Modal */}
        <Dialog
          open={!!view360}
          onClose={() => setView360(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: "#000",
              height: "90vh",
            },
          }}
        >
          {view360 && (
            <>
              <DialogTitle sx={{ color: "white", textAlign: "center", position: "relative", p: 2 }}>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {view360.name} - 360° Interactive View
                </Typography>
                <IconButton
                  onClick={() => setView360(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, backgroundColor: "#000", height: "100%" }}>
                <TileViewer360
                  tileName={view360.name}
                  tileImages={view360.images360}
                  onClose={() => setView360(null)}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default CPFittingsPage;
